import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaUsers, FaChartArea, FaServer, FaUserCheck, FaWhmcs, FaUserShield,
  FaInfo, FaLock, FaPowerOff, FaQuestionCircle, FaTicketAlt, FaRegBell,
  FaBookReader, FaBuffer, FaHouseDamage, FaStream, Fa500Px, FaMoneyBillWave,
  FaDonate, FaCcAmazonPay, FaPlug, FaTable, FaArtstation, FaAudible,
  FaInbox, FaNewspaper, FaBuilding, FaFileContract, FaWrench, FaStore,
  FaInternetExplorer, FaEye, FaHouseUser, FaMoneyCheck, FaTachometerAlt,
  FaBars, FaChevronRight, FaSignOutAlt, FaGlobe, FaBolt, FaFileInvoiceDollar,
} from "react-icons/fa";
import logo from "../../images/modon.png";
import sysJwt from "../../utils/sysJwt";
import "../../styles/modern.css";

const NavItem = ({ to, icon, label, collapsed }) => {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + "/");
  return (
    <div className="nav-item-wrapper">
      <Link
        to={to}
        className={`nav-link-item${active ? " active" : ""}`}
        title={collapsed ? label : undefined}
      >
        <span className="nav-icon">{icon}</span>
        <span className="nav-link-text">{label}</span>
      </Link>
    </div>
  );
};

const SubMenu = ({ icon, label, children, collapsed }) => {
  const location = useLocation();
  const isChildActive = React.Children.toArray(children).some(
    (child) =>
      child.props?.to &&
      (location.pathname === child.props.to || location.pathname.startsWith(child.props.to + "/"))
  );
  const [open, setOpen] = useState(isChildActive);

  if (collapsed) {
    return (
      <div className="nav-item-wrapper">
        <button className="nav-link-item" title={label}>
          <span className="nav-icon">{icon}</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="nav-item-wrapper">
        <button
          className={`nav-link-item${isChildActive ? " active" : ""}`}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="nav-icon">{icon}</span>
          <span className="nav-link-text">{label}</span>
          <FaChevronRight className={`nav-chevron${open ? " open" : ""}`} />
        </button>
      </div>
      <div className={`nav-submenu${open ? " open" : ""}`}>
        {children}
      </div>
    </>
  );
};

const Aside = ({ username, isLoading, handleLogout }) => {
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("cma_language", lng);
  };

  const currentLang = i18n.language?.split("-")[0] || "en";

  return (
    <>
      {/* Floating hamburger — only visible on mobile when sidebar is closed */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
      >
        <FaBars />
      </button>

      {/* Overlay */}
      <div
        className={`sidebar-overlay${mobileOpen ? " visible" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      <nav className={`cma-sidebar${collapsed ? " collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", padding: "0 8px 0 4px", borderBottom: "1px solid var(--sidebar-border)", minHeight: "var(--topbar-height)" }}>
          <a href="/dashboard/home" className="sidebar-brand" style={{ flex: 1, minWidth: 0 }}>
            <img src={logo} alt="CMA" className="sidebar-brand-logo" />
            <span className="sidebar-brand-text">
              Compounds System
              <small>Management Portal</small>
            </span>
          </a>
          <button
            className="sidebar-toggle-btn desktop-only"
            onClick={() => setCollapsed((c) => !c)}
            title="Toggle sidebar"
          >
            <FaBars />
          </button>
          <button
            className="sidebar-toggle-btn mobile-only"
            onClick={() => setMobileOpen(false)}
            title="Close menu"
          >
            <FaBars />
          </button>
        </div>

        {/* Nav */}
        <div className="sidebar-nav">
          {/* Home */}
          <NavItem to="/dashboard/home" icon={<FaChartArea />} label={t("nav.dashboard")} collapsed={collapsed} />

          {/* System */}
          {sysJwt.isAuthorized(["CABD"]) && (
            <SubMenu icon={<FaWhmcs />} label={t("nav.system")} collapsed={collapsed}>
              {sysJwt.isAuthorized(["CABD", "CABE"]) && (
                <NavItem to="/dashboard/usermanagement" icon={<FaUsers />} label={t("nav.users")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CABD", "CAC5"]) && (
                <NavItem to="/dashboard/rolemanagement" icon={<FaUserShield />} label={t("nav.roles")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CABD", "CAD5"]) && (
                <NavItem to="/dashboard/systemparameters" icon={<FaServer />} label={t("nav.systemParameters")} collapsed={collapsed} />
              )}
              {/* Audit */}
              {sysJwt.isAuthorized(["CB71"]) && (
                <NavItem to="/dashboard/audit" icon={<FaEye />} label={t("nav.audit")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CB8D"]) && (
                <NavItem to="/dashboard/logs" icon={<FaStream />} label={t("nav.logs")} collapsed={collapsed} />
              )}
            </SubMenu>
          )}

          {/* Definitions */}
          {sysJwt.isAuthorized(["CAF2"]) && (
            <SubMenu icon={<FaBookReader />} label={t("nav.definitions")} collapsed={collapsed}>
              {sysJwt.isAuthorized(["CAF2", "CB04"]) && (
                <NavItem to="/dashboard/faq" icon={<FaQuestionCircle />} label={t("nav.faqs")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB10"]) && (
                <NavItem to="/dashboard/notification" icon={<FaRegBell />} label={t("nav.notifications")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB50"]) && (
                <NavItem to="/dashboard/News" icon={<FaNewspaper />} label={t("nav.news")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB60"]) && (
                <NavItem to="/dashboard/GuestServices" icon={<FaWrench />} label={t("nav.guestServices")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB69"]) && (
                <NavItem to="/dashboard/internetsubscribetypes" icon={<FaInternetExplorer />} label={t("nav.internetSubscribeTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB12"]) && (
                <NavItem to="/dashboard/category" icon={<FaBuffer />} label={t("nav.category")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB16"]) && (
                <NavItem to="/dashboard/Subcategory" icon={<FaStream />} label={t("nav.subcategory")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB1E"]) && (
                <NavItem to="/dashboard/owners" icon={<FaUserCheck />} label={t("nav.owners")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB59"]) && (
                <NavItem to="/dashboard/UnitTypes" icon={<FaBuilding />} label={t("nav.unitTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB1A"]) && (
                <NavItem to="/dashboard/units" icon={<FaHouseDamage />} label={t("nav.units")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB25"]) && (
                <NavItem to="/dashboard/expensetypes" icon={<Fa500Px />} label={t("nav.expenseTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB47"]) && (
                <NavItem to="/dashboard/incometypes" icon={<FaAudible />} label={t("nav.incomeTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB43"]) && (
                <NavItem to="/dashboard/paytypes" icon={<FaArtstation />} label={t("nav.serviceTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB5C"]) && (
                <NavItem to="/dashboard/Offers" icon={<FaFileContract />} label={t("nav.offers")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB64"]) && (
                <NavItem to="/dashboard/Vendors" icon={<FaStore />} label={t("nav.vendors")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB73"]) && (
                <NavItem to="/dashboard/paymethod" icon={<FaMoneyCheck />} label={t("nav.paymentMethods")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB76"]) && (
                <NavItem to="/dashboard/servicespaymethod" icon={<FaTable />} label={t("nav.servicesPaymentMethod")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB78"]) && (
                <NavItem to="/dashboard/tickettype" icon={<FaTicketAlt />} label={t("nav.ticketTypes")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB7E"]) && (
                <NavItem to="/dashboard/employees" icon={<FaArtstation />} label={t("nav.employees")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB84"]) && (
                <NavItem to="/dashboard/electricityitems" icon={<FaPlug />} label={t("nav.electricityItems")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB89"]) && (
                <NavItem to="/dashboard/chargerestrictions" icon={<FaTable />} label={t("nav.chargerestrictions")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB8F"]) && (
                <NavItem to="/dashboard/electricityborrowingconfig" icon={<FaBolt />} label={t("nav.electricityBorrowingConfig")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB98"]) && (
                <NavItem to="/dashboard/compoundservicemap" icon={<FaTable />} label={t("nav.compoundServiceMap")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CAF2", "CB9B"]) && (
                <NavItem to="/dashboard/compoundreports" icon={<FaNewspaper />} label={t("nav.compoundReports")} collapsed={collapsed} />
              )}
            </SubMenu>
          )}

          {/* Payments */}
          {(sysJwt.isAuthorized(["CB33"]) || sysJwt.isAuthorized(["CB95"])) && (
            <SubMenu icon={<FaCcAmazonPay />} label={t("nav.payments")} collapsed={collapsed}>
              {sysJwt.isAuthorized(["CB33"]) && (
                <NavItem to="/dashboard/payments" icon={<FaCcAmazonPay />} label={t("nav.payments")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CB34"]) && (
                <NavItem to="/dashboard/unitbillsdashboard" icon={<FaMoneyBillWave />} label={t("nav.unitBills")} collapsed={collapsed} />
              )}
              {sysJwt.isAuthorized(["CB95"]) && (
                <NavItem to="/dashboard/bulkbills" icon={<FaFileInvoiceDollar />} label={t("nav.bulkBills")} collapsed={collapsed} />
              )}
            </SubMenu>
          )}

          {/* Expenses */}
          {sysJwt.isAuthorized(["CB29"]) && (
            <NavItem to="/dashboard/expenses" icon={<FaMoneyBillWave />} label={t("nav.expenses")} collapsed={collapsed} />
          )}

          {/* Incomes */}
          {sysJwt.isAuthorized(["CB4B"]) && (
            <NavItem to="/dashboard/incomes" icon={<FaInbox />} label={t("nav.incomes")} collapsed={collapsed} />
          )}

          {/* Installments */}
          {sysJwt.isAuthorized(["CB2D"]) && (
            <NavItem to="/dashboard/installments" icon={<FaDonate />} label={t("nav.installments")} collapsed={collapsed} />
          )}

          {/* Tickets */}
          {sysJwt.isAuthorized(["CB3E"]) && (
            <SubMenu icon={<FaTicketAlt />} label={t("nav.tickets")} collapsed={collapsed}>
              <NavItem to="/dashboard/tickets" icon={<FaTicketAlt />} label={t("nav.ticketsList")} collapsed={collapsed} />
              {sysJwt.isAuthorized(["CB88"]) && (
                <NavItem to="/dashboard/ticketsdashboard" icon={<FaTachometerAlt />} label={t("nav.ticketsDashboard")} collapsed={collapsed} />
              )}
            </SubMenu>
          )}

          {/* Visits */}
          {sysJwt.isAuthorized(["CB6D"]) && (
            <NavItem to="/dashboard/visits" icon={<FaHouseUser />} label={t("nav.visits")} collapsed={collapsed} />
          )}

          {/* Security */}
          {sysJwt.isAuthorized(["CB6E"]) && (
            <NavItem to="/dashboard/security" icon={<FaEye />} label={t("nav.security")} collapsed={collapsed} />
          )}

          {/* Reports */}
          {sysJwt.isAuthorized(["CAEC"]) && (
            <NavItem to="/dashboard/reports" icon={<FaChartArea />} label={t("nav.reports")} collapsed={collapsed} />
          )}

          {/* About / Change Password */}
          <NavItem to="/dashboard/applicationinfo" icon={<FaInfo />} label={t("nav.aboutSystem")} collapsed={collapsed} />
          <NavItem to="/dashboard/changepassword" icon={<FaLock />} label={t("nav.changePassword")} collapsed={collapsed} />

          {/* Logout */}
          <div className="nav-item-wrapper">
            <button
              className="nav-link-item"
              onClick={handleLogout}
              title={collapsed ? t("nav.logout") : undefined}
              style={{ color: "#dc2626" }}
            >
              <span className="nav-icon" style={{ color: "#dc2626" }}><FaSignOutAlt /></span>
              <span className="nav-link-text">{t("nav.logout")}</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          {!collapsed && (
            <div className="sidebar-user">
              <div className="sidebar-user-avatar">
                {username ? username.charAt(0).toUpperCase() : "U"}
              </div>
              <div className="sidebar-user-name">{username}</div>
            </div>
          )}
          <div className="lang-switcher">
            <button
              className={`lang-btn${currentLang === "en" ? " active" : ""}`}
              onClick={() => changeLanguage("en")}
              title="English"
            >
              EN
            </button>
            <button
              className={`lang-btn${currentLang === "ar" ? " active" : ""}`}
              onClick={() => changeLanguage("ar")}
              title="العربية"
            >
              AR
            </button>
            <button
              className={`lang-btn${currentLang === "ku" ? " active" : ""}`}
              onClick={() => changeLanguage("ku")}
              title="کوردی"
            >
              KU
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Aside;
